/**
 * The role rows the user-detail "Roles & Groups" tab renders.
 *
 * ONE SOURCE. `GET /users/:id/role-assignments` is a projection of
 * `resolvePrincipal` — the same function `can()` reads — and it already
 * carries, per row, the role's true `roleType` ('system' | 'custom'), its
 * permission count as the SERVER computed it, the assignment's source, and the
 * role id every write path matches on. This module turns that answer into
 * display rows and decides which of them may carry the inline Remove control.
 * It invents nothing and looks nothing up.
 *
 * WHY IT EXISTS (PROVE-B1c-C3). The tab used to CONCATENATE two sources: a row
 * synthesised from `user.roles` (the lean membership role the adapter
 * fabricates from `api.role`) and every assignment row, each stamped
 * `roleType: 'custom'`. That was safe only while system roles were absent from
 * `user_role_assignments`. Migration `c27_one_membership_one_role.sql` §4
 * writes the platform role into that table for every membership, and §5(c)
 * refuses to drop `user_organizations.role` unless every membership holds one —
 * so every user rendered their shipped role twice, the second time badged
 * Custom with a Remove control the server answers 404 to.
 */

import type { RoleSource } from './mock-users-data';

/**
 * The fields of one `GET /users/:id/role-assignments` row this projection
 * reads. Structurally satisfied by `UserRoleAssignment` in `use-roles.ts`;
 * declared here so the derivation can be tested on response shapes without
 * dragging react-query in.
 */
export interface UserRoleRowInput {
  assignmentId: string;
  /**
   * The role the ASSIGNMENT ROW names — the id the Remove control sends to
   * `DELETE /users/:userId/roles/:roleId`. For a retired role re-pointed at a
   * successor this is the RETIRED role; the successor arrives separately as
   * `effectiveRoleId` and is not what a removal acts on.
   */
  roleId: string;
  roleName: string;
  /** The server's answer, never a guess made here. */
  roleType: 'system' | 'custom';
  /** The count the SERVER computed, from the grants it projects everywhere. */
  permissionCount: number;
  source: string | null;
  /** ISO 8601, or null when the row carried no readable timestamp. */
  assignedAt: string | null;
}

export interface UserRoleRow {
  keyId: string;
  roleId: string;
  name: string;
  roleType: 'system' | 'custom';
  source: RoleSource | string;
  sourceDetail: string;
  grantedAt: string | null;
  permissionCount: number;
}

/**
 * `source` is the word the store holds. `user_role_assignments.source` is TEXT,
 * not the `role_source` enum, because c24 needed a `migration` value — so an
 * unknown word is expected, and it is printed rather than swallowed
 * (`RoleSourceBadge` does the same). An absent value reads as 'manual', the
 * ad-hoc admin assignment, which the badge labels "Direct".
 */
function describeSource(source: RoleSource | string): string {
  if (source === 'manual' || source === 'direct') return 'Directly assigned';
  if (source === 'inherited') return 'Inherited';
  return `via ${source}`;
}

/** One assignment row in, one display row out. Never more, never fewer. */
export function toUserRoleRows(
  assignments: readonly UserRoleRowInput[],
): UserRoleRow[] {
  return assignments.map((a) => {
    const source = (a.source ?? 'manual') as RoleSource;
    return {
      // The assignment id: unique per row on file, so two roles — or one role
      // held twice — cannot collide in a React list.
      keyId: a.assignmentId,
      roleId: a.roleId,
      name: a.roleName,
      roleType: a.roleType,
      source,
      sourceDetail: describeSource(source),
      grantedAt: a.assignedAt,
      permissionCount: a.permissionCount,
    };
  });
}

/**
 * May this row carry the inline Remove control?
 *
 * A SYSTEM role never does, and that is the design, not a convenience: system
 * roles change through the Edit User dialog (separation-of-duties), and
 * `DELETE /users/:userId/roles/:roleId` refuses them — a platform role's
 * `organization_id` is NULL, so the tenant-scoped lookup answers 404. The gate
 * reads the server's `roleType`, so the screen and that endpoint cannot
 * disagree about which rows are removable.
 *
 * Self-actions are refused server-side with 403; hidden here too, so the
 * button does not appear at all.
 */
export function canRemoveRoleRow(
  row: Pick<UserRoleRow, 'roleType' | 'roleId'>,
  caller: { canAssignRole: boolean; isSelf: boolean },
): boolean {
  return (
    row.roleType === 'custom' &&
    !!row.roleId &&
    caller.canAssignRole &&
    !caller.isSelf
  );
}
